import {
  Box,
  Button,
  HStack,
  Input,
  NativeSelect,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react'
import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'

import { Checkbox, Field, Modal } from '@mangovoice/mango-ui'

import { useScheduleI18n } from '../../i18n'
import type {
  ScheduleEvent,
  ScheduleModalNotification,
  ScheduleRetrieve,
  ScheduleUpdate,
} from '../../types'
import {
  dsText,
  EXTENDED_SCHEDULE_TIMEZONE_OPTIONS,
  fontSize,
  type ScheduleTimeZoneOption,
  tokenVar,
} from '../../utils'
import {
  createScheduleModalNotifier,
  scheduleModalInputStyles,
  scheduleModalInvalidInputStyles,
  scheduleModalLabelStyles,
} from '../shared'

export interface EditScheduleModalProps {
  isOpen: boolean
  onClose: () => void
  schedule: ScheduleRetrieve
  events: ScheduleEvent[]
  onSave: (data: ScheduleUpdate, updateCustomEvents: boolean) => void
  isSaving?: boolean
  timeZoneOptions?: ScheduleTimeZoneOption[]
  onNotify?: (notification: ScheduleModalNotification) => void
}

interface FormState {
  name: string
  description: string
  timeZone: string
}

export const EditScheduleModal: React.FC<EditScheduleModalProps> = ({
  isOpen,
  onClose,
  schedule,
  events,
  onSave,
  isSaving = false,
  timeZoneOptions = EXTENDED_SCHEDULE_TIMEZONE_OPTIONS,
  onNotify,
}) => {
  const { text } = useScheduleI18n()
  const toaster = useMemo(
    () => createScheduleModalNotifier(onNotify),
    [onNotify],
  )

  // Form state
  const [formState, setFormState] = useState<FormState>(() => ({
    name: schedule.name || '',
    description: schedule.description || '',
    timeZone: schedule.time_zone || 'America/New_York',
  }))

  // Track if timezone has changed
  const [updateCustomEvents, setUpdateCustomEvents] = useState(true)
  const [nameError, setNameError] = useState<{ message: string } | null>(null)
  const nameInputRef = useRef<HTMLInputElement>(null)
  const nameErrorId = useId()

  useEffect(() => {
    if (nameError) nameInputRef.current?.focus()
  }, [nameError])

  // Reset form when schedule changes or modal opens
  useEffect(() => {
    if (isOpen) {
      setFormState({
        name: schedule.name || '',
        description: schedule.description || '',
        timeZone: schedule.time_zone || 'America/New_York',
      })
      setUpdateCustomEvents(true)
      setNameError(null)
    }
  }, [isOpen, schedule])

  // Check if timezone has changed from original
  const timezoneChanged = formState.timeZone !== schedule.time_zone

  // Count custom events (BUSY type events that would be affected)
  const customEventsCount = useMemo(() => {
    return events.filter((e) => e.fbtype !== 'FREE').length
  }, [events])

  // Handle field updates
  const updateField = useCallback(
    <K extends keyof FormState>(field: K, value: FormState[K]) => {
      setFormState((prev) => ({ ...prev, [field]: value }))
    },
    [],
  )

  // Handle save
  const handleSave = useCallback(() => {
    // Validate
    if (!formState.name.trim()) {
      const message = text(
        'schedule.modal.editSchedule.validation.nameRequired',
        'Please enter a name for the schedule',
      )
      setNameError({ message })
      toaster.create({
        title: text('schedule.common.validationErrorTitle', 'Validation Error'),
        description: message,
        type: 'error',
        duration: 3000,
        closable: true,
      })
      return
    }

    // Build update data
    const updateData: ScheduleUpdate = {
      name: formState.name.trim(),
      description: formState.description.trim(),
      time_zone: formState.timeZone,
    }

    onSave(updateData, timezoneChanged && updateCustomEvents)
  }, [formState, onSave, text, timezoneChanged, toaster, updateCustomEvents])

  return (
    <Modal
      open={isOpen}
      onOpenChange={(details) => !details.open && onClose()}
      title={text('schedule.modal.editSchedule.title', 'Edit Schedule')}
      placement="top"
      maxWidth="500px"
      footer={
        <HStack gap={3} w="full">
          <Button
            variant="outline"
            onClick={onClose}
            flex={1}
            h="44px"
            borderColor={tokenVar('border.default')}
            bg={tokenVar('bg.default')}
            color={tokenVar('fg.default')}
            borderRadius="4px"
            fontFamily={dsText.Body1_Bold.fontFamily}
            fontSize={fontSize(dsText.Body1_Bold.fontSize)}
            fontWeight={dsText.Body1_Bold.fontWeight}
            lineHeight={dsText.Body1_Bold.lineHeight}
            _hover={{ bg: tokenVar('gray.subtle') }}
          >
            {text('schedule.common.cancel', 'Cancel')}
          </Button>
          <Button
            colorPalette="brand"
            onClick={handleSave}
            disabled={isSaving}
            flex={1}
            h="44px"
            borderRadius="4px"
            fontFamily={dsText.Body1_Bold.fontFamily}
            fontSize={fontSize(dsText.Body1_Bold.fontSize)}
            fontWeight={dsText.Body1_Bold.fontWeight}
            lineHeight={dsText.Body1_Bold.lineHeight}
          >
            {isSaving
              ? text('schedule.common.saving', 'Saving...')
              : text(
                  'schedule.modal.editSchedule.updateSchedule',
                  'Update Schedule',
                )}
          </Button>
        </HStack>
      }
    >
      <VStack gap={5} align="stretch">
        {/* Name Field */}
        <Field.Root
          required
          invalid={!!nameError}
          ids={{ errorText: nameErrorId }}
          gap={2}
        >
          <Field.Label style={scheduleModalLabelStyles}>
            {text('schedule.common.name', 'Name')}
          </Field.Label>
          <Input
            ref={nameInputRef}
            aria-describedby={nameError ? nameErrorId : undefined}
            value={formState.name}
            onChange={(e) => {
              updateField('name', e.target.value)
              setNameError(null)
            }}
            placeholder={text(
              'schedule.modal.editSchedule.namePlaceholder',
              'Business Hours',
            )}
            px={3}
            {...scheduleModalInputStyles}
            {...scheduleModalInvalidInputStyles}
          />
          {nameError && <Field.ErrorText>{nameError.message}</Field.ErrorText>}
        </Field.Root>

        {/* Description Field */}
        <Field.Root gap={2}>
          <Field.Label style={scheduleModalLabelStyles}>
            {text('schedule.common.description', 'Description')}
          </Field.Label>
          <Textarea
            value={formState.description}
            onChange={(e) => updateField('description', e.target.value)}
            placeholder={text(
              'schedule.modal.editSchedule.descriptionPlaceholder',
              'Optional description for this schedule',
            )}
            rows={3}
            px={3}
            {...scheduleModalInputStyles}
          />
        </Field.Root>

        {/* Timezone Field */}
        <Field.Root required gap={2}>
          <Field.Label style={scheduleModalLabelStyles}>
            {text('schedule.common.timeZone', 'Time Zone')}
          </Field.Label>
          <NativeSelect.Root>
            <NativeSelect.Field
              value={formState.timeZone}
              onChange={(e) => updateField('timeZone', e.target.value)}
              px={3}
              {...scheduleModalInputStyles}
            >
              {timeZoneOptions.map((tz) => (
                <option key={tz.value} value={tz.value}>
                  {tz.label}
                </option>
              ))}
            </NativeSelect.Field>
            <NativeSelect.Indicator color={tokenVar('fg.muted')} />
          </NativeSelect.Root>
        </Field.Root>

        {/* Timezone Change Warning */}
        {timezoneChanged && (
          <Box
            bg="bg.warning"
            p={4}
            borderRadius="md"
            borderWidth="1px"
            borderColor="border.warning"
          >
            <Text mb={3} fontSize="sm" color="fg.default">
              {text(
                'schedule.modal.editSchedule.timezoneWarning',
                'This will update your Weekly Hours to the selected timezone.',
              )}
            </Text>

            {customEventsCount > 0 && (
              <>
                <Text
                  mb={2}
                  fontWeight="semibold"
                  fontSize="sm"
                  color="fg.default"
                >
                  {text(
                    'schedule.modal.editSchedule.applyTimezoneChanges',
                    'Apply Timezone Changes to:',
                  )}
                </Text>
                <Checkbox
                  checked={updateCustomEvents}
                  onCheckedChange={(details) =>
                    setUpdateCustomEvents(!!details.checked)
                  }
                  display="inline-flex"
                  alignItems="center"
                  gap={0}
                >
                  <Text as="span" style={scheduleModalLabelStyles}>
                    {text(
                      'schedule.modal.editSchedule.customEventsWithCount',
                      'Custom Events ({{count}})',
                      { count: customEventsCount },
                    )}
                  </Text>
                </Checkbox>
                <Text mt={2} fontSize="xs" color="fg.muted">
                  {text(
                    'schedule.modal.editSchedule.customEventsHelp',
                    'Uncheck this option if you do not wish to update the timezone for existing Custom Events.',
                  )}
                </Text>
              </>
            )}
          </Box>
        )}
      </VStack>
    </Modal>
  )
}
